import React from 'react';

/**
 * Modal para adicionar turnos aos colaboradores
 * Suporta: Normal, Folga, Férias, Falta, Gestão, Reunião, Apoio, Baixa
 */
const HorarioAdicionarTurnoModal = ({
  show,
  modalTurno,
  isTrocaModal,
  setIsTrocaModal,
  calcularHoras,
  addTurno,
  setColaboradoresHorarios,
  onClose
}) => {
  if (!show || !modalTurno) return null;

  // Parser de horário
  const parseHorario = (horario) => {
    const regex = /(\d{1,2})(?:h|:)?(\d{0,2})?m?\s*[-–]\s*(\d{1,2})(?:h|:)?(\d{0,2})?m?/;
    const match = horario.toLowerCase().match(regex);
    
    if (match) {
      const entrada = `${match[1].padStart(2, '0')}:${(match[2] || '00').padStart(2, '0')}`;
      const saida = `${match[3].padStart(2, '0')}:${(match[4] || '00').padStart(2, '0')}`;
      return { entrada, saida };
    }
    return null;
  };

  // Adicionar turno com horário (Normal ou Apoio)
  const adicionarTurnoComHorario = (tipo) => {
    const input = document.getElementById('horario-input');
    const horario = input?.value?.trim();
    
    if (!horario) {
      alert(`Digite o horário primeiro para ${tipo === 'normal' ? 'Normal' : 'Apoio'}!`);
      return;
    }
    
    const parsed = parseHorario(horario);
    
    if (parsed) {
      const { entrada, saida } = parsed;
      
      setColaboradoresHorarios(prev => {
        return prev.map(c => {
          if (String(c.colaborador_id) !== String(modalTurno.colabId)) return c;
          
          // DEEP CLONE para evitar mutação
          const registosAtualizados = JSON.parse(JSON.stringify(c.registos_por_dia || {}));
          if (!registosAtualizados[modalTurno.day]) registosAtualizados[modalTurno.day] = [];
          
          // VALIDAR duplicação
          const jaExiste = registosAtualizados[modalTurno.day].some(t => 
            t.local === modalTurno.seccao && 
            t.tipo === tipo && 
            t.entrada === entrada && 
            t.saida === saida
          );
          
          if (jaExiste) {
            alert('⚠️ Este turno já existe nesta célula!');
            return c;
          }
          
          const novoTurno = {
            local: modalTurno.seccao,
            ...(tipo === 'normal' && { e_troca: isTrocaModal }),
            tipo: tipo,
            entrada: entrada,
            saida: saida,
            horas: parseFloat(calcularHoras(entrada, saida)),
            observacao: null
          };
          
          registosAtualizados[modalTurno.day].push(novoTurno);
          console.log(`✅ Turno ${tipo} adicionado para ${c.colaborador_nome}: ${entrada}-${saida} no dia ${modalTurno.day}, seção ${modalTurno.seccao}`);
          return { ...c, registos_por_dia: registosAtualizados };
        });
      });
      
      onClose();
    } else {
      alert('Formato inválido! Use: 8h-13h ou 8:00-13:30');
    }
  };

  // Adicionar turno simples (sem horário)
  const adicionarTurnoSimples = (tipo) => {
    addTurno(modalTurno.colabId, modalTurno.day, modalTurno.seccao, tipo);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
        <h3 className="text-xl font-bold mb-4">Adicionar Turno</h3>
        
        {/* Campo de horário para Normal e Apoio */}
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <label className="block text-sm font-medium mb-2">Horário (para Normal ou Apoio):</label>
          <input
            id="horario-input"
            type="text"
            placeholder="Ex: 8h-13h, 7h30m-13h15m, 8:00-13:30"
            className="w-full p-3 border-2 border-blue-300 rounded-lg focus:border-blue-500 outline-none"
          />
          <div className="text-xs text-gray-600 mt-1">Digite o horário e clique em Normal ou Apoio</div>
        </div>
        
        {/* Checkbox para marcar como TROCA */}
        <div className="mb-4 p-3 bg-orange-50 border-2 border-orange-300 rounded-lg">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              id="e-troca-checkbox"
              type="checkbox"
              checked={isTrocaModal}
              onChange={(e) => setIsTrocaModal(e.target.checked)}
              className="w-5 h-5 cursor-pointer"
            />
            <span className="text-sm font-bold text-orange-900 flex items-center gap-1">
              <span>🔄</span>
              <span>Este turno é uma TROCA de turno?</span>
            </span>
          </label>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => adicionarTurnoComHorario('normal')}
            className="p-4 bg-blue-100 hover:bg-blue-200 rounded-lg font-medium transition"
          >
            Normal
          </button>
          <button
            onClick={() => adicionarTurnoSimples('folga')}
            className="p-4 bg-yellow-100 hover:bg-yellow-200 rounded-lg font-medium transition"
          >
            Folga
          </button>
          <button
            onClick={() => adicionarTurnoSimples('folga_compensacao')}
            className="p-4 bg-yellow-100 hover:bg-yellow-200 rounded-lg font-medium transition border-2 border-yellow-400"
            title="Folga de compensação — conta 8h para o banco de horas"
          >
            Folga Comp. <span className="text-xs opacity-70">(8h)</span>
          </button>
          <button
            onClick={() => adicionarTurnoSimples('ferias')}
            className="p-4 bg-green-100 hover:bg-green-200 rounded-lg font-medium transition"
          >
            Férias
          </button>
          <button
            onClick={() => adicionarTurnoSimples('falta')}
            className="p-4 bg-red-100 hover:bg-red-200 rounded-lg font-medium transition"
          >
            Falta
          </button>
          <button
            onClick={() => adicionarTurnoSimples('gestao')}
            className="p-4 bg-orange-100 hover:bg-orange-200 rounded-lg font-medium transition"
          >
            Gestão
          </button>
          <button
            onClick={() => adicionarTurnoSimples('reuniao')}
            className="p-4 bg-purple-100 hover:bg-purple-200 rounded-lg font-medium transition"
          >
            Reunião
          </button>
          <button
            onClick={() => adicionarTurnoComHorario('apoio')}
            className="p-4 bg-indigo-100 hover:bg-indigo-200 rounded-lg font-medium transition"
          >
            Apoio
          </button>
          <button
            onClick={() => adicionarTurnoSimples('baixa')}
            className="p-4 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition"
          >
            Baixa
          </button>
        </div>
        <button
          onClick={onClose}
          className="w-full mt-4 p-3 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default HorarioAdicionarTurnoModal;
